import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "@emisgroup/acp-application-intl";
import { useDispatch } from "react-redux";
import { ProgressSpinner } from "../../../ProgressSpinner/ProgressSpinner";
import { ProgressSpinnerSlice } from "../../../ProgressSpinner/Index";
import { TableDataRowProps, TableHeaderProps, TableProps } from "../../../Common/UI/Index";
import { StatusTag } from "../../../Common/StatusTag/StatusTag";
import { ExecutionDetail } from "../ExecutionDetail/ExecutionDetail";
import { ErrorAlertSlice } from "../../../ErrorAlert/Index";
import { ExecutionResult, useExecutionHistoryList } from "../../../../service/hooks";
import { STATUS_POLL, ScreenMode, IExecutionHistory, IExecutionHistoryList } from "../ExecutionDetail/Types";
import { UniqueId } from "../../../../common/GenerateId";
import { ExecutionListView } from "./ExecutionList.view";

// Responsible for rendering new execution list
export const ExecutionList: React.FC = () => {

  const { t: translate } = useTranslation();
  const dispatch = useDispatch();
  const abortController = new AbortController();
  const signal = abortController.signal;

  // NOTE: Direct DOM query to get the currently selected tab's ID from the tablist.
  // This is a workaround to access tab state that isn't passed through React props.
  // Ideally, this should be refactored to use React state/context instead of DOM manipulation.
  const selectedHeader = (document.querySelector(
    '.qms-extract-tab div[role="tablist"] button[aria-selected="true"]'
  ) as HTMLElement);

  const idRaw = selectedHeader?.getAttribute("id") || undefined;
  const id = idRaw?.split("-trigger-")[1] || "";

  let artefactId = sessionStorage.getItem("selectedArtefactId") ?? "";

  const areTheyEqual = id === artefactId;

  if (!areTheyEqual) {
    sessionStorage.setItem("selectedArtefactId", id);
    artefactId = id;
  }

  const selectedExecutionConfigurationId = sessionStorage.getItem(artefactId) ?? "";

  const [executionList, setExecutionList] = useState<TableProps>({
    headerRow: [],
    dataRow: []
  } as TableProps);
  const [listView, setListView] = useState<boolean>(true);
  const [executionConfigurationId, setExecutionConfigurationId] = useState<string>("");
  const [mode, setMode] = useState<ScreenMode>(ScreenMode.ADD);
  const listRef = useRef<string>("executionList");
  const executionListRequest = useExecutionHistoryList(artefactId, signal);

  const tableHeader: TableHeaderProps[] = [
    {
      title: translate("ExecutionList.Heading.Name"),
      propertyName: "name",
      width: "31%",
    },
    {
      title: "",
      propertyName: "executionId"
    },
    {
      title: "",
      propertyName: "id"
    },
    {
      title: translate("ExecutionList.Heading.Date"),
      propertyName: "createdDate",
      width: "10%",
      sortable: false
    },
    {
      title: translate("ExecutionList.Heading.By"),
      propertyName: "createdBy",
      width: "10%",
      sortable: false
    },
    {
      title: translate("ExecutionList.Heading.Type"),
      propertyName: "type",
      width: "15%",
      sortable: false
    },
    {
      title: translate("ExecutionList.Heading.Status"),
      propertyName: "status",
      width: "10%",
      sortable: false,
    },
    {
      title: translate("ExecutionList.Heading.Population"),
      propertyName: "population",
      width: "8%",
      sortable: false
    },
    {
      title: translate("ExecutionList.Heading.Included"),
      propertyName: "included",
      width: "8%",
      sortable: false
    },
    {
      title: translate("ExecutionList.Heading.Excluded"),
      propertyName: "excluded",
      width: "8%",
      sortable: false
    },
  ];

  const onAddingNewExecution = (): void => {
    setListView(false);
    setMode(ScreenMode.ADD);
  };

  const onReturnToListClick = (): void => {
    sessionStorage.removeItem("selectedExecutionConfigurationId");
    sessionStorage.removeItem(artefactId);
    if (mode === ScreenMode.EDIT) {
      setListView(false);
      setMode(ScreenMode.VIEW);
    } else {
      setListView(true);
      setMode(ScreenMode.ADD);
    }
  };

  const populateTableData = (executionList: IExecutionHistory[]): TableDataRowProps[] => {
    return executionList.map((execution: IExecutionHistory) => {
      const executionRow: TableDataRowProps = {
        row: [
          {
            value: execution.name,
            propertyName: "name"
          },
          {
            value: <div className="execution-list__table_header_id">{execution.id}</div>,
            propertyName: "executionId"
          },
          {
            value: <div className="execution-list__table_header_id">{execution.configurationId}</div>,
            propertyName: "id"
          },
          {
            value: execution.createdDate,
            propertyName: "createdDate"
          },
          {
            value: execution.createdBy,
            propertyName: "createdBy"
          },
          {
            value: execution.type,
            propertyName: "type"
          },
          {
            value: <StatusTag displayText={execution.status}/>,
            propertyName: "status"
          },
          {
            value: execution.counts?.population ?? "-",
            propertyName: "population"
          },
          {
            value: execution.counts?.included ?? "-",
            propertyName: "included"
          },
          {
            value: execution.counts?.excluded ?? "-",
            propertyName: "excluded"
          }
        ]
      };
      return executionRow;
    });
  };

  const refetchList = (): void => {
    if (!signal.aborted) {
      executionListRequest.refetch();
    }
  };

  const getList = (): void => {
    if (executionListRequest.error) {
      dispatch(ErrorAlertSlice.actions.createErrorAlert({
        title: "Error",
        message: [executionListRequest.error["errors"][0].message || translate("UnknownError")],
        type: "error"
      }));
    } else {
      const data: IExecutionHistoryList = (executionListRequest.data as ExecutionResult<IExecutionHistoryList>).data;
      if (data.executions) {
        const table = {
          headerRow: tableHeader,
          dataRow: populateTableData(data.executions)
        };
        setExecutionList(table);
        if (data.needsRefresh) {
          setTimeout(() => refetchList(), STATUS_POLL);
        } else {
          listRef.current = `executionList-${UniqueId()}`;
        }
      }
    }
  };

  const onRowSelected = (e: React.SyntheticEvent): void => {
    const executionId = (e.currentTarget.children[1] as HTMLElement).innerText;
    const configurationId = (e.currentTarget.children[2] as HTMLElement).innerText;

    setExecutionConfigurationId(configurationId);

    sessionStorage.setItem("selectedExecutionId", executionId);
    sessionStorage.setItem("selectedExecutionConfigurationId", configurationId);
    sessionStorage.setItem(artefactId, configurationId);

    setMode(ScreenMode.VIEW);
    setListView(false);
  };

  useEffect(() => {
    if (selectedExecutionConfigurationId) {
      setExecutionConfigurationId(selectedExecutionConfigurationId);
      setMode(ScreenMode.VIEW);
      setListView(false);
    }
    return () => {
      abortController.abort();
    };
  }, [selectedExecutionConfigurationId]);

  useEffect(() => {
    if (!signal.aborted && executionListRequest.isLoading) {
      dispatch(ProgressSpinnerSlice.actions.showProgressBar(translate("LoadingMessage.ExecutionList")));
    }
  }, [executionListRequest.isLoading]);

  useEffect(() => {
    if (!signal.aborted && !executionListRequest.isLoading && !executionListRequest.isRefetching) {
      void getList();
      dispatch(ProgressSpinnerSlice.actions.hideProgressBar());
    }
  }, [executionListRequest.isLoading, executionListRequest.isRefetching]);

  return (
    <>
      {listView &&
          <ExecutionListView
            key={listRef.current}
            onAddExecutionClick={onAddingNewExecution}
            onRowSelected={onRowSelected}
            executionList={executionList}
          />
      }
      {!listView &&
          <ExecutionDetail
            onReturnToListClick={onReturnToListClick}
            mode={mode}
            setMode={setMode}
            executionConfigurationId={executionConfigurationId}
          />
      }
      <ProgressSpinner/>
    </>
  );
};
